import { createSelector } from "reselect";
import moment from "moment";
import * as helpers from "../helpers";
import * as STEP_KEYS from "../consts/stepKeys";
import {
  deepGet,
  getDatePart,
  getYearMonthPart,
  getDistinctDateFormats
} from "../../../utils";

const configSelector = state => state.config;
const bookingSelector = state => state.booking;
const appointmentSelector = state => state.appointment;
const dataSourceSelector = state => state.dataSource;
const waitingListSelector = state => state.waitingList;

const getState = (config, booking, appointment, dataSource, waitingList) => {
  const timeSlots = helpers.filterTimeSlotsByLocationAndPractitioner(
    dataSource.timeSlots,
    appointment.location,
    appointment.practitioner
  );

  let text = "";
  if (deepGet(appointment, ["appointmentDateTime", "startDateTime"])) {
    text = moment
      .parseZone(appointment.appointmentDateTime.startDateTime)
      .format("hh:mma, Do MMM YYYY");
  }

  const distinctDates = getDistinctDateFormats(
    timeSlots.map(ts => moment(ts.startDateTime)),
    "YYYY-MM-DD"
  );
  const availableDays = distinctDates.map(date => {
    return getDatePart(moment(date).toDate());
  }, []);

  const anyPractitioner =
    appointment[STEP_KEYS.PRACTITIONER] === "ANY" ||
    !appointment[STEP_KEYS.PRACTITIONER];
  const anyLocation =
    appointment[STEP_KEYS.LOCATION] === "ANY" ||
    !appointment[STEP_KEYS.LOCATION];
  let availableLocations = [appointment[STEP_KEYS.LOCATION]];

  if (anyLocation) {
    availableLocations = helpers
      .filterLocationsBySchedules(
        dataSource.locations,
        helpers.filterSchedulesByPractitioner(
          dataSource.schedules,
          appointment[STEP_KEYS.PRACTITIONER]
        )
      )
      .map(l => l.locationId);
  }

  let availablePractitioners = [appointment[STEP_KEYS.PRACTITIONER]];

  if (anyPractitioner) {
    const { practitioners, schedules } = dataSource;
    const { specialtyType } = appointment;
    const practitionersFilteredBySpecialty = helpers.filterPractitionersBySpecialty(
      practitioners,
      specialtyType
    );

    availablePractitioners = helpers
      .filterPractitionersBySchedules(
        practitionersFilteredBySpecialty,
        helpers.filterSchedulesByLocation(schedules, appointment.location)
      )
      .map(p => p.practitionerId);
  }
  const noSlots = timeSlots.length === 0;
  const loading = dataSource.loadingTimeSlots;
  const placeholder =
    text === "" && noSlots && !loading
      ? config.noSlotsPlaceholder || "[No Slots Available]"
      : "[select date & time]";

  let isPhoneConsult = false;

  if (
    config.siteConsultTypes.length > 0 &&
    appointment.location &&
    appointment.location !== "ANY"
  ) {
    isPhoneConsult = config.siteConsultTypes.some(
      type => type.siteId === appointment.location && type.consultType === 1
    );
  }

  let initialMonth = getYearMonthPart(new Date());

  if (timeSlots.length > 0) {
    initialMonth = getYearMonthPart(new Date(timeSlots[0].startDateTime));
  }

  const selectProps = {
    text,
    placeholder,
    hintText: config.appointmentDateTimeHintText,
    value: deepGet(appointment, [STEP_KEYS.APPOINTMENT_DATE_TIME]),
    completed: booking.completedSteps.includes(STEP_KEYS.APPOINTMENT_DATE_TIME),
    reload: !booking.reload
  };

  const drawerProps = {
    isOpen: booking.controlVisibility[STEP_KEYS.APPOINTMENT_DATE_TIME]
  };
  const calendarProps = { initialMonth, availableDays };

  const timeSlotsProps = {
    loading,
    timeSlots,
    practitioner: appointment.practitioner,
    location: appointment.location,
    timezone: appointment.timezone,
    anyPractitioner,
    availablePractitioners,
    anyLocation,
    availableLocations,
    isPhoneConsult,
    earliestBookingTime: config.earliestBookingTime,
    noSlots
  };

  const waitingListProps = {
    waitingListEnabled: config.waitingListEnabled,
    isWaitingListRequired: waitingList.required,
    waitingListPreferredTime: waitingList.preferredTime
  };

  return {
    ...selectProps,
    ...drawerProps,
    ...calendarProps,
    ...timeSlotsProps,
    ...waitingListProps
  };
};

const scheduleSelector = createSelector(
  configSelector,
  bookingSelector,
  appointmentSelector,
  dataSourceSelector,
  waitingListSelector,
  getState
);

export { scheduleSelector };



// WEBPACK FOOTER //
// ./src/modules/BookingPortal/selectors/scheduleSelector.js